/**
 * Palladium.Forms — Client-side form submission v4
 * Document-level submit delegation on form.palladium-form.
 *
 * This is the mechanism that makes forms work on STATICALLY rendered pages
 * (no Blazor circuit → FormContainerWidget.OnAfterRenderAsync never runs, so
 * its inline listener is never attached). On interactive pages the widget's
 * own capture-phase listener runs first and stops propagation, so this
 * delegated handler never double-fires.
 *
 * v4: sends pageContentItemId/nodeId (the server REJECTS submissions it cannot
 *     resolve to a stored form node — recipient/webhook/action are trusted
 *     server-side only), multipart branch for file uploads, inline error box
 *     instead of alert(), loading text parity with the widget script.
 */
(function () {

    function showError(formEl, msg) {
        var existing = formEl.parentNode && formEl.parentNode.querySelector('.pd-form-error');
        if (existing) existing.remove();
        var div = document.createElement('div');
        div.className = 'pd-form-error';
        div.style.cssText = 'padding:12px;margin-top:8px;background:rgba(239,68,68,0.08);border:1px solid rgba(239,68,68,0.3);color:#ef4444;border-radius:6px;text-align:center;font-size:14px;';
        div.textContent = msg;
        if (formEl.parentNode) formEl.parentNode.insertBefore(div, formEl.nextSibling);
        setTimeout(function () { div.remove(); }, 8000);
    }

    function handleSubmit(e) {
        var formEl = e.target;
        e.preventDefault();

        // HTML5 validation
        if (!formEl.checkValidity()) {
            formEl.reportValidity();
            return;
        }

        var config = {
            formName: formEl.getAttribute('data-form-name') || '',
            successMessage: formEl.getAttribute('data-success-message') || 'Envoyé avec succès.',
            redirectUrl: formEl.getAttribute('data-redirect-url') || '',
            pageContentItemId: formEl.getAttribute('data-page-id') || '',
            nodeId: formEl.getAttribute('data-node-id') || '',
        };

        // Collect fields
        var formData = new FormData(formEl);
        var data = {};
        formData.forEach(function (value, key) {
            if (data[key]) {
                if (!Array.isArray(data[key])) data[key] = [data[key]];
                data[key].push(value);
            } else {
                data[key] = value;
            }
        });

        // Unchecked checkboxes with name => send "false"
        formEl.querySelectorAll('input[type="checkbox"][name]').forEach(function (cb) {
            if (!data[cb.name]) data[cb.name] = 'false';
        });

        // Disable submit button
        var btn = formEl.querySelector('[type="submit"]');
        var originalHtml = '';
        if (btn) {
            originalHtml = btn.innerHTML;
            btn.innerHTML = btn.getAttribute('data-loading-text') || 'Envoi...';
            btn.disabled = true;
        }

        // Forms with a chosen file go multipart so files actually upload;
        // otherwise the lighter JSON path. The server resolves recipient/
        // webhook/template/action from the stored node via these ids.
        var hasFiles = Array.prototype.slice.call(formEl.querySelectorAll('input[type="file"]'))
            .some(function (i) { return i.files && i.files.length > 0; });

        // reCAPTCHA v3 : jeton invisible quand le formulaire l'active (data-captcha) ET que le site l'a activé.
        var captchaP = (formEl.getAttribute('data-captcha') === '1' && window.PdCaptcha && window.PdCaptcha.enabled())
            ? window.PdCaptcha.token('form_submit') : Promise.resolve(null);

        var request = captchaP.then(function (captchaToken) {
            if (hasFiles) {
                var mfd = new FormData(formEl);
                mfd.append('__formName', config.formName);
                mfd.append('__pageContentItemId', config.pageContentItemId);
                mfd.append('__nodeId', config.nodeId);
                if (captchaToken) mfd.append('pd_captcha_token', captchaToken);
                formEl.querySelectorAll('input[type="checkbox"][name]').forEach(function (cb) {
                    if (!cb.checked) mfd.append(cb.name, 'false');
                });
                return fetch('/api/forms/submit-multipart', {
                    method: 'POST',
                    credentials: 'same-origin',
                    body: mfd,
                });
            }
            return fetch('/api/forms/submit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'same-origin',
                body: JSON.stringify({
                    formName: config.formName,
                    pageContentItemId: config.pageContentItemId,
                    nodeId: config.nodeId,
                    captchaToken: captchaToken || '',
                    fields: data,
                }),
            });
        });

        request
            .then(function (res) {
                return res.json().then(function (body) { return { ok: res.ok, body: body }; });
            })
            .then(function (r) {
                if (r.ok && r.body.success) {
                    if (config.redirectUrl) { window.location.href = config.redirectUrl; return; }
                    formEl.innerHTML = '<div style="padding:20px;background:rgba(34,197,94,0.08);border:1px solid rgba(34,197,94,0.3);color:#16a34a;border-radius:8px;text-align:center;font-size:15px;">✓ ' + config.successMessage + '</div>';
                } else {
                    showError(formEl, r.body.error || "Erreur lors de l'envoi.");
                }
            })
            .catch(function (err) {
                console.error('[Palladium.Forms] Submit error:', err);
                showError(formEl, 'Erreur de connexion. Veuillez réessayer.');
            })
            .finally(function () {
                if (btn && formEl.contains(btn)) {
                    btn.innerHTML = originalHtml;
                    btn.disabled = false;
                }
            });
    }

    // ── Conditions d'affichage (« Afficher si… », retour Babylone 22/10/2026) ──────────────────────────
    // Tout élément d'un formulaire portant data-pf-if="<champ>" (+ data-pf-op, data-pf-val « a|b ») n'est
    // visible que si la réponse à ce champ remplit la condition. Masqué = display:none ET champs désactivés :
    // ni envoyés ni contrôlés par le navigateur (le serveur applique la même règle, FormConditions.cs).
    // data-pf-scroll="true" : la page défile jusqu'à l'élément quand il apparaît après une réponse.

    function pfForms() {
        return Array.prototype.slice.call(document.querySelectorAll('form[data-form-name], form.palladium-form'))
            .filter(function (f) { return f.querySelector('[data-pf-if]'); });
    }

    function pfValues(form, name) {
        var vals = [];
        Array.prototype.forEach.call(form.elements || [], function (el) {
            if (!el.name || el.name !== name || el.disabled) return;
            var t = (el.type || '').toLowerCase();
            if (t === 'checkbox' || t === 'radio') { if (el.checked) vals.push(el.value || 'true'); }
            else if (el.tagName === 'SELECT' && el.multiple) {
                Array.prototype.forEach.call(el.options, function (o) { if (o.selected && o.value) vals.push(o.value); });
            }
            else if (el.value) vals.push(el.value);
        });
        return vals.map(function (v) { return String(v).trim(); })
            .filter(function (v) { return v && v.toLowerCase() !== 'false'; });
    }

    function pfSatisfied(form, el) {
        var field = (el.getAttribute('data-pf-if') || '').trim();
        if (!field) return true;
        var op = (el.getAttribute('data-pf-op') || 'eq').trim().toLowerCase();
        var wanted = (el.getAttribute('data-pf-val') || '').split('|')
            .map(function (v) { return v.trim().toLowerCase(); }).filter(Boolean);
        var actual = pfValues(form, field).map(function (v) { return v.toLowerCase(); });
        var any = actual.some(function (a) { return wanted.indexOf(a) !== -1; });
        switch (op) {
            case 'filled': return actual.length > 0;
            case 'empty': return actual.length === 0;
            case 'neq': return !any;
            case 'contains':
                return wanted.length === 0 ? actual.length > 0
                    : wanted.some(function (w) { return actual.some(function (a) { return a.indexOf(w) !== -1; }); });
            default: return wanted.length === 0 ? actual.length > 0 : any;
        }
    }

    function pfIsControl(el) { return /^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName); }

    function pfSetVisible(form, el, visible) {
        var hidden = el.getAttribute('data-pf-hidden') === '1';
        if (visible === !hidden) return false;
        var targets = [el];
        if (el.id && pfIsControl(el) && window.CSS && CSS.escape) {
            Array.prototype.forEach.call(form.querySelectorAll('label[for="' + CSS.escape(el.id) + '"]'),
                function (l) { targets.push(l); });
        }
        targets.forEach(function (t) {
            if (!visible) {
                t.setAttribute('data-pf-display', t.style.display || '');
                t.style.display = 'none';
            } else {
                t.style.display = t.getAttribute('data-pf-display') || '';
                t.removeAttribute('data-pf-display');
            }
        });
        var controls = pfIsControl(el) ? [el] : Array.prototype.slice.call(el.querySelectorAll('input, select, textarea'));
        controls.forEach(function (c) {
            if (!visible && !c.disabled) { c.disabled = true; c.setAttribute('data-pf-disabled', '1'); }
            else if (visible && c.getAttribute('data-pf-disabled') === '1') { c.disabled = false; c.removeAttribute('data-pf-disabled'); }
        });
        if (visible) el.removeAttribute('data-pf-hidden'); else el.setAttribute('data-pf-hidden', '1');
        return true;
    }

    function pfApply(form, fromUser) {
        var shown = [];
        // Plusieurs passes : un champ masqué compte pour vide, ce qui peut masquer ce qui dépend de lui.
        for (var pass = 0; pass < 6; pass++) {
            var changed = false;
            Array.prototype.forEach.call(form.querySelectorAll('[data-pf-if]'), function (el) {
                var visible = pfSatisfied(form, el);
                if (pfSetVisible(form, el, visible)) {
                    changed = true;
                    if (visible) shown.push(el); else shown = shown.filter(function (x) { return x !== el; });
                }
            });
            if (!changed) break;
        }
        if (!fromUser) return;
        var target = shown.filter(function (el) { return el.getAttribute('data-pf-scroll') === 'true'; })[0];
        if (target && target.scrollIntoView) {
            try { target.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { target.scrollIntoView(); }
            var first = pfIsControl(target) ? target : target.querySelector('input, select, textarea');
            if (first && first.focus) setTimeout(function () { try { first.focus({ preventScroll: true }); } catch (e) { } }, 350);
        }
    }

    function pfApplyAll(fromUser) { pfForms().forEach(function (f) { pfApply(f, fromUser); }); }

    var pfTimer = 0;
    function pfSchedule(e) {
        var form = e && e.target && e.target.closest ? e.target.closest('form') : null;
        if (!form || !form.querySelector('[data-pf-if]')) return;
        clearTimeout(pfTimer);
        // Laisse un composant interactif (liste déroulante maison) recopier sa valeur dans le select natif.
        pfTimer = setTimeout(function () { pfApply(form, true); }, 40);
    }
    ['input', 'change', 'click', 'keyup'].forEach(function (type) { document.addEventListener(type, pfSchedule, true); });

    // ── Pré-remplissage par l'adresse : ?<nom du champ>=valeur (champ vide seulement) ──
    function pfPrefill() {
        var params;
        try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
        if (!params || !params.toString()) return;
        Array.prototype.forEach.call(document.querySelectorAll('form[data-form-name], form.palladium-form'), function (form) {
            if (form.__pfPrefilled) return;
            form.__pfPrefilled = true;
            params.forEach(function (value, key) {
                Array.prototype.forEach.call(form.elements || [], function (el) {
                    if (el.name !== key || el.type === 'hidden' || el.type === 'file' || el.type === 'password') return;
                    var t = (el.type || '').toLowerCase();
                    if (t === 'checkbox' || t === 'radio') {
                        if (String(el.value).toLowerCase() !== String(value).toLowerCase()) return;
                        el.checked = true;
                    } else if (el.tagName === 'SELECT') {
                        if (el.value || !Array.prototype.some.call(el.options, function (o) { return o.value === value; })) return;
                        el.value = value;
                    } else {
                        if (el.value) return;
                        el.value = value;
                    }
                    try { el.dispatchEvent(new Event('change', { bubbles: true })); } catch (e) { }
                });
            });
        });
    }

    function pfInit() { pfPrefill(); pfApplyAll(false); }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', pfInit);
    else pfInit();
    // Navigation améliorée Blazor et rendus interactifs : réévalue quand un formulaire arrive.
    document.addEventListener('enhancedload', pfInit);
    if (window.MutationObserver) {
        var pfMo = new MutationObserver(function (records) {
            for (var i = 0; i < records.length; i++) {
                var added = records[i].addedNodes;
                for (var j = 0; j < added.length; j++) {
                    var n = added[j];
                    if (n.nodeType === 1 && ((n.matches && n.matches('form, [data-pf-if]'))
                        || (n.querySelector && n.querySelector('[data-pf-if]')))) {
                        clearTimeout(pfTimer); pfTimer = setTimeout(pfInit, 60); return;
                    }
                }
            }
        });
        var pfStartMo = function () { if (document.body) pfMo.observe(document.body, { childList: true, subtree: true }); };
        if (document.body) pfStartMo(); else document.addEventListener('DOMContentLoaded', pfStartMo);
    }
    window.PalladiumForms = window.PalladiumForms || {};
    window.PalladiumForms.applyConditions = pfApplyAll;

    // Attach via event delegation (works with Blazor dynamic rendering AND
    // static SSR). Match on data-form-name — the widget's functional marker —
    // not on .palladium-form: converted imported forms (PdImported) drop that
    // class on purpose to keep their original design, but they still submit.
    document.addEventListener('submit', function (e) {
        var form = e.target;
        if (form.tagName === 'FORM'
            && (form.hasAttribute('data-form-name') || form.classList.contains('palladium-form'))) {
            handleSubmit(e);
        }
    });

})();
